import {
  faArrowsRotate,
  faBox,
  faBoxOpen,
  faCheckCircle,
  faCreditCard,
  faExclamationCircle,
  faEye,
} from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { CircularProgress } from 'src/components/CircularProgress';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { ServiceCount } from 'src/models/package/_types';
import {
  fetchVisitAvailablePackages,
  fetchVisitPackage,
  updateVisit,
} from 'src/models/visit/visit';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import type { Visit } from 'src/models/visit/_types';
import type { Identifier } from 'types/utilityTypes';

interface VisitPackageInfoProps {
  visit: Visit;
  serviceId: Identifier;
  onChargePackage?: (visit: Visit) => void;
}

/**
 * VisitPackageInfo - Compact package display for visits
 *
 * Streamlined layout:
 * - Package name and status on one line
 * - Service counts in a compact format
 * - Swap functionality in collapsible panel
 */
export const VisitPackageInfo: FC<VisitPackageInfoProps> = ({
  visit,
  serviceId,
  onChargePackage,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.viewEventDialog.visitPackageInfo',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });
  const queryClient = useQueryClient();
  const [isSwapping, setIsSwapping] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<Identifier | null>(
    null
  );

  // Fetch visit's current package
  const { data: visitPackage, isLoading: isLoadingPackage } = useQuery({
    queryKey: ['visitPackage', visit.id],
    queryFn: () => fetchVisitPackage(visit.id),
    enabled: !!visit.id,
  });

  // Fetch available packages for swapping
  const { data: availablePackages = [], isLoading: isLoadingAvailable } =
    useQuery({
      queryKey: ['visitAvailablePackages', visit.id],
      queryFn: () => fetchVisitAvailablePackages(visit.id),
      enabled: !!visit.id,
    });

  // Swap package mutation (matches v2 savePackageSwap action)
  const swapPackageMutation = useMutation({
    mutationFn: async (packageId: Identifier) => {
      return updateVisit({
        id: visit.id,
        paidByPackageId: packageId,
        determinePackageId: packageId,
      });
    },
    onSuccess: () => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['visitPackage', visit.id] });
      queryClient.invalidateQueries({
        queryKey: ['visitAvailablePackages', visit.id],
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      toast.success(t('swappedSuccess'));
      setSelectedPackageId(null);
      setIsSwapping(false);
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('swapFailed')));
    },
  });

  const handleSaveSwap = () => {
    if (selectedPackageId) {
      swapPackageMutation.mutate(selectedPackageId);
    }
  };

  const displayPackage = selectedPackageId
    ? availablePackages.find((p) => p.id === selectedPackageId) || visitPackage
    : visitPackage;

  const matchingServiceCount = displayPackage?.serviceCounts?.find(
    (sc: ServiceCount) => sc.serviceIds?.includes(serviceId)
  );

  const isClosedOut =
    visit.status === 'completed' || visit.status === 'noshowed';

  if (isLoadingPackage) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size="small" />
        <Typography variant="caption" color="text.secondary">
          {t('loadingPackage')}
        </Typography>
      </Box>
    );
  }

  // No package found - show different messages based on visit state
  if (!displayPackage?.id) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {isClosedOut ? (
          // Closed-out visit with no package
          visit.paid ? (
            <Typography variant="caption" color="text.secondary">
              <Icon icon={faBoxOpen} size="xsmall" /> {t('noPackageUsed')}
            </Typography>
          ) : (
            <Box
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Icon icon={faExclamationCircle} size="xsmall" color="error" />
              <Typography variant="caption" color="error.main">
                {t('noPackageFound')}
              </Typography>
            </Box>
          )
        ) : (
          // Active visit with no package
          <Typography variant="caption" color="text.secondary">
            <Icon icon={faBoxOpen} size="xsmall" color="primary" />{' '}
            {t('noPackageUsedLower')}
          </Typography>
        )}
        {visit.userId && onChargePackage && (
          <Button
            variant="borderless"
            size="small"
            onClick={() => onChargePackage(visit)}
            startIcon={<Icon icon={faCreditCard} />}
          >
            {t('assignPackage')}
          </Button>
        )}
      </Box>
    );
  }

  // Format service count info
  const getCountsDisplay = () => {
    if (!matchingServiceCount) return null;
    if (matchingServiceCount.count === 0) {
      return t('usedOfUnlimited', { used: matchingServiceCount.used });
    }
    return t('usedOfCount', {
      used: matchingServiceCount.used,
      count: matchingServiceCount.count,
    });
  };

  const countsDisplay = getCountsDisplay();

  return (
    <Box>
      {/* Main package info row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Package info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          {isClosedOut && visit.paidByPackageId ? (
            <Icon icon={faCheckCircle} size="xsmall" color="success" />
          ) : (
            <Icon icon={faBox} size="xsmall" color="primary" />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" fontWeight="medium">
                {displayPackage.name} (#{displayPackage.id}){' '}
                {!isClosedOut && !visit.paidByPackageId && (
                  <Typography variant="caption" color="text.secondary">
                    · {t('packageThatMightBeUsed')}
                  </Typography>
                )}
              </Typography>

              {isClosedOut && visit.paidByPackageId && (
                <Typography
                  variant="caption"
                  color="success.main"
                  fontWeight="medium"
                >
                  · {t('used')}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {countsDisplay && (
                <Typography variant="caption" color="text.secondary">
                  {countsDisplay}
                </Typography>
              )}
              {(matchingServiceCount?.rolloverVisits ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('plusRollover', {
                    rolloverVisits: matchingServiceCount?.rolloverVisits ?? 0,
                  })}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                ·{' '}
                {displayPackage.expiresOn
                  ? t('expires', {
                      date: format(
                        new Date(displayPackage.expiresOn),
                        'MMM d, yy'
                      ),
                    })
                  : t('never')}
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* View package */}
          {visit.clientId && (
            <Tooltip title={t('viewPackage')}>
              <Link
                href={`/ex4/clients/${visit.clientId}/packages`}
                target="_blank"
              >
                <Icon icon={faEye} size="small" />
              </Link>
            </Tooltip>
          )}

          {/* Swap package */}
          {availablePackages.length > 0 && !isSwapping && (
            <Tooltip title={t('swapPackage')}>
              <Button
                variant="borderless"
                size="small"
                onClick={() => setIsSwapping(true)}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                <Icon icon={faArrowsRotate} size="tiny" />
              </Button>
            </Tooltip>
          )}

          {/* Charge/Assign package */}
          {!isSwapping && visit.userId && onChargePackage && (
            <Tooltip title={t('chargeOrAssignPackage')}>
              <Button
                variant="borderless"
                size="small"
                onClick={() => onChargePackage(visit)}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                <Icon icon={faCreditCard} size="small" />
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Box>
      {/* Swap package UI */}
      {isSwapping && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: 'background.level1',
            borderRadius: 1,
          }}
        >
          {isLoadingAvailable ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size="small" />
              <Typography variant="caption">{t('loadingPackages')}</Typography>
            </Box>
          ) : (
            <>
              <TextField
                size="small"
                label={t('selectPackage')}
                value={selectedPackageId || ''}
                onChange={(e) => setSelectedPackageId(e.target.value || null)}
                options={[
                  { value: '', label: t('originalPackage') },
                  ...availablePackages.map((pkg) => ({
                    value: pkg.id,
                    label: `${pkg.name} (#${pkg.id})`,
                  })),
                ]}
                fullWidth
              />
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1 }}
                justifyContent="flex-end"
              >
                <Button
                  variant="borderless"
                  size="small"
                  onClick={() => {
                    setSelectedPackageId(null);
                    setIsSwapping(false);
                  }}
                  disabled={swapPackageMutation.isPending}
                >
                  {tCommon('cancel')}
                </Button>
                {isClosedOut && selectedPackageId && (
                  <Button
                    variant="prominent"
                    size="small"
                    onClick={handleSaveSwap}
                    disabled={swapPackageMutation.isPending}
                  >
                    {swapPackageMutation.isPending
                      ? t('savingEllipsis')
                      : t('save')}
                  </Button>
                )}
                {!isClosedOut && selectedPackageId && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ alignSelf: 'center' }}
                  >
                    {t('checkInToSaveSwap')}
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
